import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

function stopPreview(reference) {
  const audio = reference.current;
  reference.current = null;
  if (!audio) return;
  audio.onerror = null;
  audio.pause();
  audio.removeAttribute("src");
  audio.load();
}

export default function VoiceSelect({ value, onChange, disabled, volume, onAvailabilityChange }) {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewError, setPreviewError] = useState("");
  const preview = useRef(null);

  useEffect(() => {
    let active = true;
    let timer;
    const controller = new AbortController();
    const load = () => axios.get("/api/voices", { signal: controller.signal }).then(response => {
      if (!active) return;
      if (response.data.loading) {
        timer = setTimeout(load, 1000);
        return;
      }
      setVoices(response.data.voices || []);
      setLoading(false);
    }).catch(() => {
      if (active) {
        setVoices([]);
        setLoading(false);
      }
    });
    load();
    return () => {
      active = false;
      clearTimeout(timer);
      controller.abort();
      stopPreview(preview);
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    onAvailabilityChange(voices.length > 0);
    const legacyKey = ["ash", "fable", "onyx", "echo"].includes(value) ? "james" : "tia";
    const selected = voices.find(voice => voice.key === value)
      || voices.find(voice => voice.key === legacyKey) || voices[0];
    const nextValue = selected?.key || "";
    if (nextValue !== value) onChange(nextValue);
  }, [loading, voices, value, onChange, onAvailabilityChange]);

  useEffect(() => {
    if (disabled) stopPreview(preview);
  }, [disabled]);

  useEffect(() => {
    if (preview.current) preview.current.volume = volume;
  }, [volume]);

  const handleChange = event => {
    stopPreview(preview);
    setPreviewError("");
    const voice = voices.find(candidate => candidate.key === event.target.value);
    if (!voice) return;
    onChange(voice.key);
    if (!voice.previewUrl) return;
    const audio = new Audio(voice.previewUrl);
    audio.volume = volume;
    preview.current = audio;
    const failed = () => {
      if (preview.current === audio) setPreviewError("Voice preview unavailable.");
    };
    audio.onerror = failed;
    audio.play().catch(failed);
  };

  return <>
    <select id="voiceSelect" aria-label="Voice" value={voices.some(voice => voice.key === value) ? value : ""}
      onChange={handleChange} disabled={disabled || loading || voices.length === 0}>
      {voices.length === 0 && <option value="">{loading ? "Loading voices..." : "Voices unavailable"}</option>}
      {voices.map(voice => <option key={voice.key} value={voice.key}>{voice.label}</option>)}
    </select>
    {previewError && <span role="alert">{previewError}</span>}
  </>;
}